import React, { useState } from 'react';
import {
  XMarkIcon,
  CubeIcon,
  TrashIcon,
  TagIcon,
  PlusIcon,
  MinusIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { SlotInfo } from './tipos';

interface ModalGestionSlotProps {
  slot: SlotInfo | null;
  isOpen: boolean;
  onClose: () => void;
  onIniciarReubicacion: (slot: SlotInfo) => void;
  onAjustarStock?: (slot: SlotInfo, itemId: string, delta: number) => Promise<void> | void;
  onCambiarEstado?: (slot: SlotInfo, itemId: string, nuevoEstado: string) => Promise<void> | void;
  onDespacharPallet?: (slot: SlotInfo) => Promise<void> | void;
  onVerRackCompleto?: (slot: SlotInfo) => void;
}

export const ModalGestionSlot: React.FC<ModalGestionSlotProps> = ({
  slot,
  isOpen,
  onClose,
  onIniciarReubicacion,
  onAjustarStock,
  onDespacharPallet,
  onVerRackCompleto,
}) => {
  const [ejecutando, setEjecutando] = useState(false);

  if (!isOpen || !slot) return null;

  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-end bg-black/50 backdrop-blur-sm transition-all animate-fadeIn">
      {/* Slide-over panel */}
      <div
        className="w-full max-w-md h-full bg-slate-900 border-l border-slate-700/80 shadow-2xl flex flex-col text-white overflow-hidden animate-slideLeft"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1. Header con Ubicación WMS */}
        <div className="p-5 border-b border-slate-800 bg-slate-950/80 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-blue-500/20 text-blue-400 border border-blue-500/30">
                WMS SLOTTING
              </span>
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                  slot.isOccupied
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-slate-700 text-slate-300'
                }`}
              >
                {slot.isOccupied ? 'Carga Asignada' : 'Hueco Disponible'}
              </span>
            </div>
            <h3 className="text-lg font-black text-white tracking-tight">
              {slot.zona} · {slot.pasillo} · {slot.rack}
            </h3>
            <p className="text-xs font-mono font-bold text-cyan-400">
              Nivel {slot.nivel} · Celda S{slot.posicion} ({slot.slotKey})
            </p>
            {onVerRackCompleto && (
              <button
                type="button"
                onClick={() => {
                  onVerRackCompleto(slot);
                  onClose();
                }}
                className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-blue-600/25 hover:bg-blue-600/40 border border-blue-400/40 text-blue-300 font-bold text-[11px] transition-all"
                title="Abrir telemetría y mapa de posiciones completo del rack"
              >
                📊 Ver Rack Completo / Heatmap
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 2. Contenido del Slot */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs">
          {slot.isOccupied ? (
            <>
              {/* Tarjeta de Licencia de Pallet (LPN) */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-950 to-slate-900 border border-blue-500/30 shadow-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TagIcon className="w-4 h-4 text-cyan-400" />
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                      License Plate Number (LPN)
                    </span>
                  </div>
                  <span className="font-mono text-xs font-black text-cyan-300 px-2 py-0.5 bg-cyan-950/60 rounded border border-cyan-500/30">
                    {slot.lpn}
                  </span>
                </div>

                {/* Métricas del Pallet */}
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-800">
                  <div className="bg-slate-800/60 p-2 rounded-xl text-center">
                    <span className="text-[10px] text-slate-400 block font-semibold">Stock Total</span>
                    <span className="text-sm font-extrabold text-white">{slot.cantidadTotal} u</span>
                  </div>
                  <div className="bg-slate-800/60 p-2 rounded-xl text-center">
                    <span className="text-[10px] text-slate-400 block font-semibold">Peso Bruto</span>
                    <span className="text-sm font-extrabold text-emerald-400">
                      {slot.pesoTotalKg.toFixed(1)} kg
                    </span>
                  </div>
                  <div className="bg-slate-800/60 p-2 rounded-xl text-center">
                    <span className="text-[10px] text-slate-400 block font-semibold">Carga Ton</span>
                    <span className="text-sm font-extrabold text-blue-400">
                      {slot.pesoTotalTon.toFixed(3)} t
                    </span>
                  </div>
                </div>
              </div>

              {/* Botón de Reubicación 3D */}
              <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-amber-300 text-xs flex items-center gap-1.5">
                    <TruckIcon className="w-4 h-4" /> Reubicar este Pallet
                  </h4>
                  <p className="text-[10px] text-slate-400">
                    Mueve la estiba completa hacia otro hueco vacío en 3D
                  </p>
                </div>
                <button
                  onClick={() => {
                    onIniciarReubicacion(slot);
                    onClose();
                  }}
                  className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs transition-all shadow-md shadow-amber-500/20"
                >
                  Mover en 3D
                </button>
              </div>

              {/* Desglose de Productos / SKUs contenidos */}
              <div className="space-y-3">
                <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 block">
                  Mercancía Almacenada ({slot.items.length} SKUs)
                </span>

                {slot.items.map((item) => (
                  <div
                    key={item.id}
                    className="p-3.5 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h5 className="font-extrabold text-white text-xs">{item.nombre}</h5>
                        <span className="text-[10px] font-mono text-cyan-400 font-semibold">
                          SKU: {item.sku}
                        </span>
                      </div>
                      <span className="text-xs font-black text-amber-400">
                        {item.cantidad} unidades
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-400 pt-1 border-t border-slate-800/80">
                      <div>
                        <span>Lote: </span>
                        <strong className="text-slate-200">{item.lote || 'N/A'}</strong>
                      </div>
                      <div>
                        <span>Categoría: </span>
                        <strong className="text-slate-200">{item.categoria || 'General'}</strong>
                      </div>
                      <div>
                        <span>Peso Unitario: </span>
                        <strong className="text-slate-200">{item.peso} kg</strong>
                      </div>
                      <div>
                        <span>Estado: </span>
                        <strong
                          className={
                            item.estado === 'Dañado'
                              ? 'text-red-400'
                              : item.estado === 'Cuarentena'
                              ? 'text-amber-400'
                              : 'text-emerald-400'
                          }
                        >
                          {item.estado || 'Disponible'}
                        </strong>
                      </div>
                    </div>

                    {/* Acciones de Picking / Ajuste por SKU */}
                    {onAjustarStock && (
                      <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                        <span className="text-[10px] text-slate-400 font-bold">Picking Rápido:</span>
                        <div className="flex items-center gap-1.5">
                          <button
                            disabled={ejecutando || item.cantidad <= 1}
                            onClick={async () => {
                              setEjecutando(true);
                              await onAjustarStock(slot, item.id, -1);
                              setEjecutando(false);
                            }}
                            className="p-1 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300"
                            title="Extraer 1 unidad (Picking)"
                          >
                            <MinusIcon className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-xs font-mono font-bold w-7 text-center">
                            {item.cantidad}
                          </span>
                          <button
                            disabled={ejecutando}
                            onClick={async () => {
                              setEjecutando(true);
                              await onAjustarStock(slot, item.id, 1);
                              setEjecutando(false);
                            }}
                            className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
                            title="Añadir 1 unidad"
                          >
                            <PlusIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Botón Despachar Pallet */}
              {onDespacharPallet && (
                <div className="pt-2">
                  <button
                    onClick={async () => {
                      if (
                        window.confirm(
                          `¿Confirmas el despacho y vaciado del Pallet ${slot.lpn} en ${slot.slotKey}?`
                        )
                      ) {
                        setEjecutando(true);
                        await onDespacharPallet(slot);
                        setEjecutando(false);
                        onClose();
                      }
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-red-600/20 hover:bg-red-600/30 border border-red-500/40 text-red-400 font-bold transition-all text-xs"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Despachar / Retirar Pallet de este Hueco
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Hueco Vacío */
            <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 rounded-3xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-400 shadow-inner">
                <CubeIcon className="w-8 h-8 opacity-60" />
              </div>
              <div className="space-y-1">
                <h4 className="font-extrabold text-sm text-white">Hueco Libre para Almacenamiento</h4>
                <p className="text-xs text-slate-400 max-w-[260px] leading-relaxed">
                  Esta posición está disponible para recibir pallets entrantes de recepción o transferencias internas.
                </p>
              </div>

              <div className="p-3 bg-slate-950/80 rounded-xl border border-slate-800 text-[11px] text-slate-300 w-full space-y-1">
                <div className="flex justify-between">
                  <span>Coordenada:</span>
                  <span className="font-mono font-bold text-cyan-400">{slot.slotKey}</span>
                </div>
                <div className="flex justify-between">
                  <span>Nivel:</span>
                  <span className="font-bold text-white">Nivel {slot.nivel}</span>
                </div>
                <div className="flex justify-between">
                  <span>Posición en viga:</span>
                  <span className="font-bold text-white">Posición {slot.posicion}</span>
                </div>
              </div>

              <p className="text-[10px] text-slate-500 italic">
                💡 Consejo: Si deseas ingresar mercancía, puedes crear un producto asignado a esta coordenada en el panel de inventario.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
